var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _StepsModulatorBoxAdapter_instances, _a, _StepsModulatorBoxAdapter_predecessor, _StepsModulatorBoxAdapter_activeSteps, _StepsModulatorBoxAdapter_wrapParameters;
import { StringMapping, ValueMapping } from "@opendaw/lib-std";
import { LfoModulatorBoxAdapter } from "./LfoModulatorBoxAdapter";
import { ModulatorBoxAdapter } from "./ModulatorBoxAdapter";
// WASM CONTRACT: mirrors the engine's `modulation::DIRECTION_*` (crates/engine/src/modulation.rs).
export var StepsDirection;
(function (StepsDirection) {
    StepsDirection[StepsDirection["Forward"] = 0] = "Forward";
    StepsDirection[StepsDirection["Backward"] = 1] = "Backward";
    StepsDirection[StepsDirection["PingPong"] = 2] = "PingPong";
    StepsDirection[StepsDirection["Alternate"] = 3] = "Alternate";
    StepsDirection[StepsDirection["Random"] = 4] = "Random";
})(StepsDirection || (StepsDirection = {}));
export class StepsModulatorBoxAdapter extends ModulatorBoxAdapter {
    constructor(context, box) {
        super(context, box);
        _StepsModulatorBoxAdapter_instances.add(this);
        this.namedParameter = __classPrivateFieldGet(this, _StepsModulatorBoxAdapter_instances, "m", _StepsModulatorBoxAdapter_wrapParameters).call(this, box);
        this.registerParameterTracks();
    }
    get count() { return this.box.count.getValue(); }
    get steps() { return this.box.steps.fields(); }
    /// The passes the sequence makes over the pattern, each covering the steps `[from, to)` it reaches.
    get passes() {
        const count = Math.max(1, Math.min(this.count, _a.MaxSteps));
        switch (this.box.direction.getValue()) {
            case StepsDirection.Backward:
                return [{ ascending: false, from: 0, to: count }];
            case StepsDirection.PingPong:
                return [{ ascending: true, from: 0, to: count }, { ascending: false, from: 0, to: count }];
            case StepsDirection.Alternate:
                return count < 3
                    ? [{ ascending: true, from: 0, to: count }]
                    : [{ ascending: true, from: 0, to: count - 1 }, { ascending: false, from: 1, to: count }];
            // Random reorders per cycle, so there is no one pass to draw: the display shows a forward one.
            default:
                return [{ ascending: true, from: 0, to: count }];
        }
    }
    patternAt(step, ascending) {
        const count = Math.max(1, Math.min(this.count, _a.MaxSteps));
        const index = ((Math.floor(step) % count) + count) % count;
        const current = this.steps[index].getValue();
        const smooth = this.box.smooth.getValue();
        if (smooth <= 0.0) {
            return current;
        }
        const local = step - Math.floor(step);
        const traversed = ascending ? local : 1.0 - local;
        const previous = this.steps[__classPrivateFieldGet(this, _StepsModulatorBoxAdapter_instances, "m", _StepsModulatorBoxAdapter_predecessor).call(this, index, count, ascending)].getValue();
        const ramp = Math.min(1.0, traversed / smooth);
        return previous + (current - previous) * ramp * ramp * (3.0 - 2.0 * ramp);
    }
    get neutral() { return this.box.bipolar.getValue() ? 0.5 : 0.0; }
    emitted(stored) { return this.box.bipolar.getValue() ? stored * 2.0 - 1.0 : stored; }
    stored(emitted) { return this.box.bipolar.getValue() ? emitted * 0.5 + 0.5 : emitted; }
    clear() {
        const neutral = this.neutral;
        __classPrivateFieldGet(this, _StepsModulatorBoxAdapter_instances, "m", _StepsModulatorBoxAdapter_activeSteps).call(this).forEach(step => step.setValue(neutral));
    }
    randomize() { __classPrivateFieldGet(this, _StepsModulatorBoxAdapter_instances, "m", _StepsModulatorBoxAdapter_activeSteps).call(this).forEach(step => step.setValue(Math.random())); }
    rotate(offset) {
        const active = __classPrivateFieldGet(this, _StepsModulatorBoxAdapter_instances, "m", _StepsModulatorBoxAdapter_activeSteps).call(this);
        const values = active.map(step => step.getValue());
        const count = values.length;
        active.forEach((step, index) => step.setValue(values[(((index - offset) % count) + count) % count]));
    }
}
_a = StepsModulatorBoxAdapter, _StepsModulatorBoxAdapter_instances = new WeakSet(), _StepsModulatorBoxAdapter_predecessor = function _StepsModulatorBoxAdapter_predecessor(index, count, ascending) {
    const wrap = (value) => ((value % count) + count) % count;
    switch (this.box.direction.getValue()) {
        case StepsDirection.PingPong:
            return ascending
                ? (index === 0 ? 0 : index - 1)
                : (index === count - 1 ? count - 1 : index + 1);
        case StepsDirection.Alternate:
            return ascending
                ? (index === 0 ? Math.min(1, count - 1) : index - 1)
                : (index === count - 1 ? Math.max(0, count - 2) : index + 1);
        default:
            return ascending ? wrap(index - 1) : wrap(index + 1);
    }
}, _StepsModulatorBoxAdapter_activeSteps = function _StepsModulatorBoxAdapter_activeSteps() {
    return this.steps.slice(0, Math.max(1, Math.min(this.count, _a.MaxSteps)));
}, _StepsModulatorBoxAdapter_wrapParameters = function _StepsModulatorBoxAdapter_wrapParameters(box) {
    return {
        rateSync: this.parametric.createParameter(box.rateSync, ValueMapping.linearInteger(0, LfoModulatorBoxAdapter.RateStrings.length - 1), StringMapping.indices("", LfoModulatorBoxAdapter.RateStrings), "Sync"),
        rateAbsolute: this.parametric.createParameter(box.rateAbsolute, ValueMapping.powerByCenter(LfoModulatorBoxAdapter.CenterAbsoluteRate, 0.0, LfoModulatorBoxAdapter.MaxAbsoluteRate), StringMapping.numeric({ unit: "Hz", fractionDigits: 2 }), "Free"),
        phase: this.parametric.createParameter(box.phase, ValueMapping.unipolar(), StringMapping.percent({ fractionDigits: 0 }), "Phase"),
        smooth: this.parametric.createParameter(box.smooth, ValueMapping.unipolar(), StringMapping.percent({ fractionDigits: 0 }), "Smooth")
    };
};
StepsModulatorBoxAdapter.MaxSteps = 64;
StepsModulatorBoxAdapter.DirectionStrings = ["Forward", "Backward", "Ping-Pong", "Alternate", "Random"];
